import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { GAME_COLLECTION_TAG_PATHS, useFramework } from '@suntex.fim/framework';
import { ModuleFilter } from '@suntex.fim/hub';

import { usePlayerContext } from '../context';
import { getFocusOptions, getModuleTagsForGradeLevels } from '../utils/focus';

const DISABLE_FIR_META_KEY = 'disable-fir';

export interface ModuleAccessFilter {
  enable: ModuleFilter;
  disable: ModuleFilter;
  /** True while school-level access settings are still being fetched. */
  isLoading: boolean;
}

/**
 * Derives module access filters from the player's focus mode, matching the
 * same logic used by the Hub to determine which modules are visible. First In
 * Reading is disabled when the player's school has a non-zero `disable-fir`
 * school meta value.
 */
export function useModuleAccessFilters(): ModuleAccessFilter {
  const { api } = useFramework();
  const { playerId, playerInfo, isPreview } = usePlayerContext();

  const enable = useMemo(
    () =>
      playerInfo
        ? getModuleTagsForGradeLevels(getFocusOptions(playerInfo))
        : undefined,
    [playerInfo],
  );

  const {
    data: isFirDisabled,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['school', 'meta', playerId, DISABLE_FIR_META_KEY],
    queryFn: () =>
      api.get_Player_SchoolMeta({ playerId, key: DISABLE_FIR_META_KEY }),
    select: (response) =>
      response.metaItems.some((item) => Boolean(item.intValue)),
    enabled: !isPreview,
  });

  const disable = useMemo<ModuleFilter>(() => {
    if (isPreview) return undefined;
    // Fail closed: block First In Reading until the lookup succeeds.
    return isLoading || isError || isFirDisabled
      ? [GAME_COLLECTION_TAG_PATHS.firstInReading]
      : undefined;
  }, [isPreview, isLoading, isError, isFirDisabled]);

  return { enable, disable, isLoading };
}
